import React, { useEffect, useState } from "react";
import styles from "./FacilityMainDashboard.module.scss";
import SidebarFacility from "../SidebarFacility";
import Loader from "../../../components/Loader/Loader";

// Helper function to get the Sunday-to-Saturday range for the current week
const getWeeklyDateRange = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysToSunday = dayOfWeek; // Number of days to rewind to Sunday
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - daysToSunday); // Set to most recent Sunday

  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6); // Set to Saturday

  return { sunday, saturday };
};

// Helper function to get the 1st to last day of the current month
const getMonthlyDateRange = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1); // 1st of current month
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month

  return { firstDay, lastDay };
};

// Helper function to format date as DD/MM/YYYY
const formatDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const FacilityMainDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hideSvg, setHideSvg] = useState(false);
  const [dateTrigger, setDateTrigger] = useState(new Date()); // State to force re-render

  const handleSvg = () => {
    setHideSvg(!hideSvg);
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      try {
        const response = await fetch(
          "https://ballersapi.ballerslife.co/api/v1/facility_admin/dashboard_view",
          {
            method: "GET",
            headers: {
              accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Update date ranges daily
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      // Only trigger re-render if the date has changed
      if (now.getDate() !== dateTrigger.getDate()) {
        setDateTrigger(now);
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, [dateTrigger]);

  // Get dynamic date ranges
  const { sunday, saturday } = getWeeklyDateRange();
  const { firstDay, lastDay } = getMonthlyDateRange();

  // Format dates for display
  const weeklyRange = `${formatDate(sunday)} - ${formatDate(saturday)}`;
  const monthlyRange = `${formatDate(firstDay)} - ${formatDate(lastDay)}`;

  return (
    <div className="dashMain">
      <div className="sideBar">
        <SidebarFacility />
      </div>
      {hideSvg ? (
        <div className="sideBarMobile">
          <SidebarFacility />
        </div>
      ) : (
        <div></div>
      )}
      {loading ? (
        <Loader />
      ) : error ? (
        <div className="error">Error: {error}</div>
      ) : (
        <div className="content">
          <div className="user" id="scrollable-container">
            <div className="svgMain" onClick={handleSvg}>
              {hideSvg ? (
                <div className="closeSvg"></div>
              ) : (
                <div className="hamburgerSvg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#0F0223"
                    stroke="#eee2e2"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-menu"
                  >
                    <line x1="4" x2="20" y1="12" y2="12" />
                    <line x1="4" x2="20" y1="6" y2="6" />
                    <line x1="4" x2="20" y1="18" y2="18" />
                  </svg>
                </div>
              )}
            </div>

            <div className="userSec">
              {hideSvg ? (
                <div onClick={handleSvg}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#0F0223"
                    stroke="#eee2e2"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-x-square"
                  >
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                    <path d="m15 9-6 6" />
                    <path d="m9 9 6 6" />
                  </svg>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
          <div className="userContent">
            <div className={styles.dashboard}>
              <div className={styles.summaryContainer}>
                <div className={styles.summaryBox}>
                  <h2>WEEKLY BOOKINGS</h2>
                  <p>({weeklyRange})</p>
                  <h3>{dashboardData?.weekly_hours || 0} HOURS</h3>
                </div>
                <div className={styles.summaryBox}>
                  <h2>MONTHLY BOOKINGS</h2>
                  <p>({monthlyRange})</p>
                  <h3>{dashboardData?.monthly_hours || 0} HOURS</h3>
                </div>
              </div>
              <div className={styles.upcomingBookings}>
                <h2>UPCOMING BOOKINGS</h2>
                <table>
                  <thead>
                    <tr>
                      <th>NAME</th>
                      <th>DATE</th>
                      <th>SPORT</th>
                      <th>TIME</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.upcoming_bookings?.map((booking, index) => (
                      <tr key={index}>
                        <td>{booking.user_id}</td>
                        <td>{booking.booking_date}</td>
                        <td>{booking.sport}</td>
                        <td>
                          {booking.booking_time}-{booking.end_time}
                        </td>
                        <td>{booking.status}</td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan="4">No bookings available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityMainDashboard;
